import { useCallback, useRef, useState } from "react";

import { isAPIError, isNetworkError, isTimeoutError, readServiceError } from "@/core/services";
import { aplicarSimulacao } from "@/modules/recursos/simulacao.gateway";
import type { RelatorioAplicacaoApi } from "@/modules/recursos/simulacao.gateway";
import { isRecord, readFirstText } from "@/core/utils";
import type {
  AlteracaoSimulada,
  CenarioSimulacao,
  DivergenciaAplicacao,
} from "@/modules/recursos/simulacao";
import { textoPapeisPendentes } from "@/modules/recursos/simulacao";
import { itensDaAplicacao, isAplicada } from "@/modules/recursos/simulacao/simulacao.aplicacao";
export type {
  EstadoAplicacao,
  ItemAplicacao,
  MotivoFalha,
} from "@/modules/recursos/simulacao/simulacao.aplicacao";

type ItemPainel = ReturnType<typeof itensDaAplicacao>[number];

/** Referência estável quando não há divergências da revalidação. */
const SEM_DIVERGENCIAS: readonly DivergenciaAplicacao[] = [];

/** RN020/RN022: o que a revalidação já descarta não depende do POST. */
function comDescartes(
  itens: ItemPainel[],
  divergencias: readonly DivergenciaAplicacao[],
): ItemPainel[] {
  return itens.map((item) => {
    const divergencia = divergencias.find(
      (candidata) => candidata.chave === item.chave && candidata.desfecho === "descartada",
    );
    return divergencia
      ? { ...item, estado: "descartada" as const, mensagem: divergencia.mensagem }
      : item;
  });
}

/**
 * Estado do Aplicar Simulação: um único POST /aplicacao. Os itens do painel
 * vêm do relatório: APLICADA encerra; ATIVA (rollback) mantém o modo. O que a
 * revalidação marcou como descartada (RN020, RN022) fica descartado; o que ela
 * marcou com aviso (RN021) sai "aplicada com aviso".
 */
export function useAplicacaoSimulacao(
  cenario: CenarioSimulacao,
  simulacaoId: string | null,
  versao: number | null,
  divergencias: readonly DivergenciaAplicacao[] = SEM_DIVERGENCIAS,
) {
  const [itens, setItens] = useState(() => itensDaAplicacao(cenario));
  const [executando, setExecutando] = useState(false);
  const [iniciada, setIniciada] = useState(false);
  const [statusCabecalho, setStatusCabecalho] = useState<string | null>(null);
  const justificativaRef = useRef("");

  const atualizar = useCallback((chave: string, mudanca: Partial<ItemPainel>) => {
    setItens((current) =>
      current.map((item) => (item.chave === chave ? { ...item, ...mudanca } : item)),
    );
  }, []);

  const aplicar = useCallback(
    async (texto: string) => {
      justificativaRef.current = texto;
      const itensAtuais = comDescartes(itensDaAplicacao(cenario), divergencias);
      setItens(itensAtuais);
      setIniciada(true);
      setStatusCabecalho(null);
      setExecutando(true);

      if (!simulacaoId || versao === null) {
        // Sem simulação persistida: nada a aplicar via API.
        setItens((current) =>
          current.map((item) =>
            item.estado === "descartada"
              ? item
              : {
                  ...item,
                  estado: "nao-aplicada" as const,
                  mensagem: "Simulação não encontrada.",
                  motivo: "negocio" as const,
                },
          ),
        );
        setExecutando(false);
        return;
      }

      try {
        const relatorio = await aplicarSimulacao(simulacaoId, versao, texto);
        setStatusCabecalho(relatorio.status);
        marcarItensDoRelatorio(itensAtuais, relatorio, atualizar, divergencias);
      } catch (error) {
        let motivo: "sistema" | "conexao" | "negocio";
        if (isNetworkError(error) || isTimeoutError(error)) {
          motivo = "conexao";
        } else if (isAPIError(error) && error.status >= 400 && error.status < 500) {
          motivo = "negocio";
        } else {
          motivo = "sistema";
        }
        const mensagem = isAPIError(error)
          ? readServiceError(error).message
          : "Erro ao aplicar simulação.";
        setItens((current) =>
          current.map((item) =>
            item.estado === "descartada"
              ? item
              : { ...item, estado: "nao-aplicada" as const, mensagem, motivo },
          ),
        );
      } finally {
        setExecutando(false);
      }
    },
    [cenario, simulacaoId, versao, divergencias, atualizar],
  );

  const restantes = useCallback(
    (): AlteracaoSimulada[] =>
      itens
        .filter((item) => !isAplicada(item))
        .map((item) => ({
          ...item.alteracao,
          ...(item.mensagem && item.estado === "nao-aplicada"
            ? { ajustePendente: item.mensagem }
            : {}),
        })),
    [itens],
  );

  const comMotivo = (motivo: string) =>
    itens.some((item) => item.estado === "nao-aplicada" && item.motivo === motivo);

  return {
    itens,
    executando,
    iniciada,
    statusCabecalho,
    aplicar,
    tentarNovamente: () => aplicar(justificativaRef.current),
    retomar: () => aplicar(justificativaRef.current),
    restantes,
    processados: itens.filter((item) => item.estado !== "pendente" && item.estado !== "aplicando")
      .length,
    todasAplicadas: itens.every(isAplicada),
    haAjustaveis:
      comMotivo("negocio") ||
      (statusCabecalho === "ATIVA" && itens.some((item) => !isAplicada(item))),
    haPendencias: itens.some(
      (item) => item.estado === "pendente" || item.estado === "nao-aplicada",
    ),
    haFalhaDeSistema: comMotivo("sistema"),
    haSemConexao: comMotivo("conexao"),
  };
}

// ---------------------------------------------------------------------------
// Mapeamento do relatório → estados dos itens
// ---------------------------------------------------------------------------

interface ItemRelatorioLido {
  chave: string;
  operacao: string;
  estado: string;
  mensagem: string;
  /** RN024: papéis obrigatórios que a equipe gravada deixou sem profissional (campo opcional do core). */
  papeisPendentes: string[];
}

/** O core devolve `status` (`naoAplicada`); o painel usa `estado` (`nao-aplicada`). */
function normalizarEstadoRelatorio(bruto: string): string {
  if (bruto === "aplicadaComAviso") return "aplicada-com-aviso";
  if (bruto === "naoAplicada") return "nao-aplicada";
  if (bruto === "pendente") return "pendente";
  return bruto;
}

function lerPapeisPendentes(rawItem: Record<string, unknown>): string[] {
  const papeis = rawItem.papeisObrigatoriosPendentes;
  if (!Array.isArray(papeis)) return [];
  return papeis
    .map((papel) => (isRecord(papel) ? readFirstText(papel, "nome").trim() : ""))
    .filter((nome) => nome !== "");
}

function lerEstadoItem(rawItem: unknown): ItemRelatorioLido | null {
  if (!isRecord(rawItem)) return null;
  const estado = normalizarEstadoRelatorio(
    (readFirstText(rawItem, "estado") || readFirstText(rawItem, "status")).trim(),
  );
  if (!estado) return null;
  return {
    chave: readFirstText(rawItem, "chave").trim(),
    operacao: readFirstText(rawItem, "operacao").trim(),
    estado,
    mensagem: readFirstText(rawItem, "mensagem").trim(),
    papeisPendentes: lerPapeisPendentes(rawItem),
  };
}

function marcarItensDoRelatorio(
  itensAtuais: ItemPainel[],
  relatorio: RelatorioAplicacaoApi,
  atualizar: (chave: string, mudanca: Partial<ItemPainel>) => void,
  divergencias: readonly DivergenciaAplicacao[],
): void {
  const ESTADOS_FINAIS = new Set(["aplicada", "aplicada-com-aviso", "nao-aplicada", "descartada"]);

  // Tenta mapear item a item pelo campo `chave` do relatório.
  const itensRelatorio = (Array.isArray(relatorio.itens) ? relatorio.itens : [])
    .map(lerEstadoItem)
    .filter((i): i is ItemRelatorioLido => i !== null);

  const usados = new Set<number>();

  function dadosDoItem(item: ItemPainel, index: number): ItemRelatorioLido | undefined {
    const porChave = itensRelatorio.findIndex(
      (relatorioItem, relatorioIndex) =>
        !usados.has(relatorioIndex) &&
        relatorioItem.chave !== "" &&
        relatorioItem.chave === item.chave,
    );
    if (porChave >= 0) {
      usados.add(porChave);
      return itensRelatorio[porChave];
    }

    const porOperacao = itensRelatorio
      .map((relatorioItem, relatorioIndex) => ({ relatorioItem, relatorioIndex }))
      .filter(
        ({ relatorioItem, relatorioIndex }) =>
          !usados.has(relatorioIndex) && relatorioItem.operacao === item.alteracao.acao,
      );
    const unico = porOperacao.length === 1 ? porOperacao[0] : undefined;
    if (unico) {
      usados.add(unico.relatorioIndex);
      return unico.relatorioItem;
    }

    if (itensRelatorio.length === itensAtuais.length && !usados.has(index)) {
      usados.add(index);
      return itensRelatorio[index];
    }

    return undefined;
  }

  for (const [index, item] of itensAtuais.entries()) {
    const dadosRelatorio = dadosDoItem(item, index);
    if (item.estado === "descartada" && !dadosRelatorio) continue;
    if (dadosRelatorio?.estado === "pendente") {
      atualizar(item.chave, { estado: "pendente", mensagem: "", motivo: null });
    } else if (dadosRelatorio && ESTADOS_FINAIS.has(dadosRelatorio.estado)) {
      const aviso = avisoDaAplicada(item, dadosRelatorio, divergencias);
      atualizar(item.chave, {
        estado: aviso ? "aplicada-com-aviso" : (dadosRelatorio.estado as ItemPainel["estado"]),
        mensagem: aviso ?? (dadosRelatorio.mensagem || "Aplicado."),
        motivo: dadosRelatorio.estado === "nao-aplicada" ? "negocio" : null,
      });
    } else if (relatorio.status === "APLICADA") {
      const aviso = avisoDaAplicada(item, undefined, divergencias);
      atualizar(item.chave, {
        estado: aviso ? "aplicada-com-aviso" : "aplicada",
        mensagem: aviso ?? "Aplicado.",
        motivo: null,
      });
    } else if (relatorio.status === "ATIVA") {
      atualizar(item.chave, { estado: "pendente", mensagem: "", motivo: null });
    } else {
      atualizar(item.chave, {
        estado: "nao-aplicada",
        mensagem: "Não foi possível aplicar.",
        motivo: "negocio",
      });
    }
  }
}

/** Aviso de quem entrou na aplicação: datas alteradas (RN021) ou papel obrigatório sem profissional (RN024). */
function avisoDaAplicada(
  item: ItemPainel,
  dados: ItemRelatorioLido | undefined,
  divergencias: readonly DivergenciaAplicacao[],
): string | null {
  if (dados && dados.estado !== "aplicada" && dados.estado !== "aplicada-com-aviso") return null;
  if (dados && dados.papeisPendentes.length > 0) return textoPapeisPendentes(dados.papeisPendentes);
  const divergencia = divergencias.find(
    (candidata) => candidata.chave === item.chave && candidata.desfecho === "aplicada-com-aviso",
  );
  return divergencia?.mensagem ?? null;
}
